import { useState, useRef, useEffect } from "react";
import { type LucideIcon, ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";

export interface TCollapsibleNavSectionProps {
    title: string;
    icon: LucideIcon;
    isExpanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    collapsed?: boolean;
    active?: boolean;
}

export function CollapsibleNavSection({
    title,
    icon: Icon,
    isExpanded,
    onToggle,
    children,
    collapsed = false,
    active = false,
}: TCollapsibleNavSectionProps) {
    // Collapsed mode shows the section as an icon that toggles a flyout dropdown
    // of its sub-items. Mirrors the InlineSwitcher pattern: the panel is rendered
    // inside the ref'd container so item clicks navigate before the outside-click
    // (mousedown) handler closes it.
    const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close the flyout whenever the sidebar leaves collapsed mode, so re-collapsing
    // doesn't reopen it without a click (and the panel always reopens from a fresh
    // click, with the ref mounted for positioning).
    useEffect(() => {
        if (!collapsed) setIsFlyoutOpen(false);
    }, [collapsed]);

    useEffect(() => {
        if (!isFlyoutOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsFlyoutOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isFlyoutOpen]);

    if (collapsed) {
        const rect = containerRef.current?.getBoundingClientRect();
        return (
            <div className="relative" ref={containerRef}>
                <button
                    onClick={() => setIsFlyoutOpen((open) => !open)}
                    title={title}
                    aria-label={title}
                    aria-haspopup="true"
                    aria-expanded={isFlyoutOpen}
                    className={cn(
                        "w-full flex items-center justify-center rounded-lg py-2.5 transition-all duration-200",
                        active
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                        isFlyoutOpen && !active && "bg-sidebar-muted text-sidebar-foreground",
                    )}
                >
                    <Icon className="h-5 w-5" />
                </button>
                {isFlyoutOpen && (
                    <div
                        className="fixed z-[9999] w-56 rounded-lg border border-slate-200 bg-white py-2 shadow-lg"
                        style={{
                            left: rect ? rect.right + 8 : undefined,
                            top: rect ? rect.top : undefined,
                        }}
                        onClick={() => setIsFlyoutOpen(false)}
                    >
                        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                            {title}
                        </p>
                        {children}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-1">
            {/* Section Header - clickable to expand/collapse */}
            <button
                onClick={onToggle}
                className={cn(
                    "w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                    "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                )}
            >
                <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4" />
                    <span>{title}</span>
                </div>
                <ChevronDown
                    className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        isExpanded && "rotate-180",
                    )}
                />
            </button>

            {/* Collapsible Content */}
            <div
                className={cn(
                    "overflow-hidden transition-all duration-200 ease-in-out",
                    isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
                )}
            >
                <div className="pl-7 space-y-1">{children}</div>
            </div>
        </div>
    );
}
